import { Switch } from '@headlessui/react'
import { motion } from 'framer-motion'
import { CurrencyIcon as CurrencyDollar, Loader2 } from 'lucide-react'
import React from 'react'
import AnimatedBackground from '../../../utils/AnimatedBackground'
import Footer from '../../../utils/Footer'
import TopBar from '../../../utils/TopBar'
import { useCryptoDashboard } from '../../hooks/dashboard/CryptoDash'

const CryptoDashboard: React.FC = () => {
  const { assetTotals, individualAssets, showUSD, setShowUSD, exchangeRate, isLoading, isRecalculating } = useCryptoDashboard()

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="flex items-center gap-2 text-blue-400">
          <Loader2 className="h-8 w-8 animate-spin" />
          <span className="text-lg">Loading crypto data...</span>
        </div>
      </div>
    );
  }

  if (!individualAssets || individualAssets.length === 0) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="text-center text-gray-400">
          <h2 className="text-xl font-semibold mb-2">No crypto data available</h2>
          <p>Please add some cryptocurrencies to see your dashboard.</p>
        </div>
      </div>
    );
  }

  const formatCurrency = (value: string, currency: string) => {
    const numValue = parseFloat(value)
    const formatter = new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })

    if (currency.toLowerCase() === 'brl' && showUSD) {
      return `$ ${formatter.format(numValue / exchangeRate)}`
    } else if (currency.toLowerCase() === 'usd' && !showUSD) {
      return `R$ ${formatter.format(numValue * exchangeRate)}`
    }
    return currency.toLowerCase() === 'brl' ? `R$ ${formatter.format(numValue)}` : `$ ${formatter.format(numValue)}`
  }

  const calculateTotal = () => {
    const total = assetTotals.reduce((acc, asset) => {
      const value = parseFloat(asset.total_invested_amount)
      if (asset.currency.toLowerCase() === 'brl' && showUSD) {
        return acc + (value / exchangeRate)
      } else if (asset.currency.toLowerCase() === 'usd' && !showUSD) {
        return acc + (value * exchangeRate)
      }
      return acc + value
    }, 0)

    const formatter = new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
    
    return `${showUSD ? '$ ' : 'R$ '}${formatter.format(total)}`
  }

  const calculateTotalCurrentValue = () => {
    const total = assetTotals.reduce((acc, asset) => {
      const value = parseFloat(asset.total_current_value)
      if (asset.currency.toLowerCase() === 'brl' && showUSD) {
        return acc + (value / exchangeRate)
      } else if (asset.currency.toLowerCase() === 'usd' && !showUSD) {
        return acc + (value * exchangeRate)
      }
      return acc + value
    }, 0)

    const formatter = new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
    
    return `${showUSD ? '$ ' : 'R$ '}${formatter.format(total)}`
  }

  const calculateTotalProfit = () => {
    return assetTotals.reduce((acc, asset) => {
      const currentValue = parseFloat(asset.total_current_value)
      const investedValue = parseFloat(asset.total_invested_amount)
      const profit = currentValue - investedValue
      
      if (asset.currency.toLowerCase() === 'brl' && showUSD) {
        return acc + (profit / exchangeRate)
      } else if (asset.currency.toLowerCase() === 'usd' && !showUSD) {
        return acc + (profit * exchangeRate)
      }
      return acc + profit
    }, 0)
  }

  const formatTotalProfit = () => {
    const profit = calculateTotalProfit()
    const formatter = new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
    return `${showUSD ? '$ ' : 'R$ '}${formatter.format(profit)}`
  }

  const calculateTotalReturn = () => {
    const totalInvested = assetTotals.reduce((acc, asset) => {
      const value = parseFloat(asset.total_invested_amount)
      if (asset.currency.toLowerCase() === 'brl' && showUSD) {
        return acc + (value / exchangeRate)
      } else if (asset.currency.toLowerCase() === 'usd' && !showUSD) {
        return acc + (value * exchangeRate)
      }
      return acc + value
    }, 0)
  
    const totalProfit = calculateTotalProfit()
    return ((totalInvested + totalProfit) / totalInvested - 1) * 100
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex flex-col relative">
      <AnimatedBackground />
      <TopBar 
        title="Crypto Dashboard"
        subtitle="Track and analyze your crypto portfolio"
      />
      {isRecalculating && (
        <div className="absolute top-[55px] right-[50px] bg-blue-500/20 px-3 py-1 rounded-md z-50 flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin text-blue-400" />
          <span className="text-blue-300 text-sm">Updating values...</span>
        </div>
      )}
      <div className="absolute top-[50px] right-[200px] flex items-center space-x-4 z-50">
        <span className="text-blue-400 font-semibold">
          1 USD = {Number(exchangeRate).toFixed(2)} BRL
        </span>
        <div className="flex items-center space-x-2">
          <span className="text-gray-300">BRL</span>
          <Switch
            checked={showUSD}
            onChange={setShowUSD}
            className={`${showUSD ? 'bg-blue-600' : 'bg-gray-600'}
              relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-800`}
          >
            <span className={`${showUSD ? 'translate-x-6' : 'translate-x-1'}
              inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
            />
          </Switch>
          <span className="text-gray-300">USD</span>
        </div>
      </div>

      <main className="overflow-x-hidden overflow-y-auto relative z-20">
        <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="bg-gray-800/30 backdrop-blur-sm shadow-lg overflow-hidden rounded-2xl border border-gray-700/50 mb-8"
          >
            <div className="px-6 py-8 sm:p-10">
              <h3 className="text-xl leading-6 font-semibold text-gray-300">Crypto Value</h3>
              <div className="mt-4 text-5xl font-bold text-blue-400">
                {calculateTotalCurrentValue()}
              </div>
              <div className="mt-4 flex justify-between text-lg">
                <span className="text-gray-400">Total Invested:</span>
                <span className="text-gray-300">{calculateTotal()}</span>
              </div>
              <div className="flex justify-between text-lg">
                <span className="text-gray-400">Total Profit:</span>
                <span className={`${calculateTotalProfit() > 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {formatTotalProfit()}
                </span>
              </div>
              <div className="mt-4 flex justify-between text-lg border-t border-gray-700 pt-4">
                <span className="text-gray-400">Total Return:</span>
                <span className={`${calculateTotalReturn() > 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {calculateTotalReturn().toFixed(2)}%
                </span>
              </div>
            </div>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {individualAssets.map((asset, index) => (
              <motion.div 
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 * index }}
                className="bg-gray-800/30 backdrop-blur-sm overflow-hidden shadow-lg rounded-xl border border-gray-700/50"
              >
                <div className="px-6 py-5">
                  <div className="flex items-center space-x-3">
                    <div className="flex-shrink-0">
                      <CurrencyDollar className="h-8 w-8 text-green-400" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-200">
                        {asset.ticker}
                      </h3>
                      <div className="text-sm text-gray-400">
                        {asset.currency.toUpperCase()}
                      </div>
                    </div>
                  </div>
                  <div className="mt-4">
                    <div className="text-2xl font-bold text-blue-400">
                      {formatCurrency(asset.current_amount, asset.currency)}
                    </div>
                    <div className="flex justify-between mt-2 text-sm">
                      <span className="text-gray-400">Invested:</span>
                      <span className="text-gray-300">
                        {formatCurrency(asset.invested_amount, asset.currency)}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-400">Profit:</span>
                      <span className={`${Number(asset.profit) > 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {formatCurrency(asset.profit, asset.currency)}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm mt-2 pt-2 border-t border-gray-700">
                      <span className="text-gray-400">Return:</span>
                      <span className={`${Number(asset.return) > 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {Number(asset.return).toFixed(2)}%
                      </span>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  )
}

export default CryptoDashboard